import { CURRENCIES, filterCurrencyOption, ROLE_TYPES } from '../../utils/DataConstants';
import {
	contractorClientsRestCrudThunksForBuyer,
	contractorClientsRestCrudThunksForSupplier,
} from '../../thunks/contractor_clients_thunks';
import { connect } from 'react-redux';
import { Button, Form, Input, message, Select } from 'antd';
import OWButton from '../ow_button/OWButton';
import { capitalize } from '../../utils/DataFormatterUtils';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
	businessTypeOptions,
	emailValidator,
	getObjectValues,
	nullSafeGet,
} from '../../utils/DataAccessUtils';
import ContractorClientFormBanner from './ContractorClientFormBanner';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import { usersRestCrudThunksForSupplier } from '../../thunks/users_thunks';
import { UserName } from '../name_component/user/UserNameComponent';
import PhoneNumberComponentAntdV4 from '../PhoneNumberComponent/PhoneNumberComponentAntdV4';
import { addressValidatorFormv4 } from '../../utils/LocationUtils';
import { LocationAutocomplete } from '../location_autocomplete/LocationAutocomplete';
import { withUserType } from '../withUserType';

const FORM_NAME = 'contractor-client-form';

const ContractorClientForm = ({
	onSuccess,
	onCancel,
	create,
	update,
	targetCollectionName,
	formData,

	users,
	fetchUsers,
}) => {
	const [form] = Form.useForm();
	const formRef = useRef();
	const [loading, setLoading] = useState(false);

	const isUpdate = useMemo(() => nullSafeGet('id', formData), [formData]);
	const submitMethod = useMemo(() => (isUpdate ? update : create), [create, isUpdate, update]);

	const handleSubmit = useCallback(
		(values) => {
			if (loading) return;
			const address = Object.keys(values)
				.filter((k) => k.indexOf('primaryAddress-') > -1)
				.reduce((acc, k) => {
					const [_, x] = k.split('-');
					acc[x] = values[k];
					return acc;
				}, {});
			const entity = {
				...(formData || {}),
				...values,
				isSupplier: true,
				...(!isUpdate ? { address } : {}),
			};
			setLoading(true);
			submitMethod(entity, 'id', targetCollectionName)
				.then((record) => {
					message.success(`Client ${isUpdate ? 'saved' : 'added'} successfully!`);
					onSuccess(record);
				})
				.catch((err) => message.error(err))
				.finally((record) => {
					setLoading(false);
				});
		},
		[formData, isUpdate, loading, onSuccess, submitMethod, targetCollectionName]
	);

	const currencyRecordsList = getObjectValues(CURRENCIES);

	const handleLocationAutocompleteSelect = useCallback(
		(addr, lonLat, parsedAddress) => {
			form.setFieldsValue({
				primaryAddress: addr,
				'primaryAddress-streetAddress1': parsedAddress.streetAddress1,
				'primaryAddress-streetAddress2Opt': parsedAddress.streetAddress2Opt,
				'primaryAddress-city': parsedAddress.city,
				'primaryAddress-region': parsedAddress.region,
				'primaryAddress-postcode': parsedAddress.postcode,
				'primaryAddress-country': parsedAddress.country,
				longitude: lonLat.lng,
				latitude: lonLat.lat,
			});
			form.validateFields(['primaryAddress']);
		},
		[form]
	);
	const initialValues = useMemo(
		() => ({
			...(formData || {}),
			isSupplier: true,
			isBuyer: false,
		}),
		[formData]
	);

	const formItemStyle = { width: 500 };

	return (
		<Form
			id={FORM_NAME}
			layout="vertical"
			requiredMark={true}
			initialValues={initialValues}
			onFinish={handleSubmit}
			form={form}
			ref={formRef}
		>
			{isUpdate ? null : <ContractorClientFormBanner />}
			<Form.Item
				name="name"
				label="Client Name"
				style={formItemStyle}
				rules={[
					{
						required: true,
						message: 'Please give your client a name.',
					},
				]}
			>
				<Input />
			</Form.Item>
			<Form.Item
				name="businessType"
				label="Type of Business"
				style={formItemStyle}
				rules={[
					{
						required: true,
						message: 'Please indicate the type of business',
					},
				]}
			>
				<Select>
					{businessTypeOptions.map((businessType) => (
						<Select.Option value={businessType.key}>{businessType.value}</Select.Option>
					))}
				</Select>
			</Form.Item>
			<Form.Item
				name="currencyId"
				label="Currency"
				style={formItemStyle}
				rules={[{ required: true, message: 'Please select a currency for this event.' }]}
			>
				<Select
					showSearch={true}
					optionFilterProp="children"
					filterOption={filterCurrencyOption}
					placeholder="Currency"
				>
					{currencyRecordsList.map((currency) => (
						<Select.Option key={currency.id} value={currency.id}>
							{capitalize(currency.displayNamePlural)} ({currency.id})
						</Select.Option>
					))}
				</Select>
			</Form.Item>
			<Form.Item
				label="Default Work Order Subscribers"
				name="defaultWorkOrderSubscribers"
				style={formItemStyle}
				required={true}
				rules={[
					{
						required: true,
						message: 'Please select at least one subscriber to this client.',
					},
				]}
			>
				<OWAsyncSelect
					mode="multiple"
					stateSlice={users}
					targetCollectionName="contractorLocationsDropdown"
					fetchMultiple={() => {}}
					fetchData={(searchText, targetCollectionName) => {
						fetchUsers({ name: searchText }, targetCollectionName);
					}}
					renderRecord={(user) => (
						<Select.Option key={user.id} value={user.id}>
							<UserName contact={user.contact} onClick={() => {}} mode={'inline'} />
						</Select.Option>
					)}
				/>
			</Form.Item>
			{!isUpdate
				? [
						<Form.Item
							label="Primary Contact Email Address"
							name={'primaryContactEmail'}
							style={formItemStyle}
							rules={[
								{ required: false, message: "Please input your primary contact's email!" },
								{ validator: emailValidator },
							]}
						>
							<Input placeholder="John.Doe@doeplumbing.com" />
						</Form.Item>,
						<PhoneNumberComponentAntdV4
							form={form}
							style={formItemStyle}
							fieldProps={{
								initialValue: formData.phoneNumber,
								name: 'phoneNumber',
								label: 'Phone Number',
							}}
						/>,
						<Form.Item
							required={true}
							label="Address"
							rules={[
								{
									required: true,
									message: 'Please input your client address!',
								},
								({ getFieldValue }) => ({
									validator: addressValidatorFormv4(getFieldValue),
								}),
							]}
							name="primaryAddress"
							style={formItemStyle}
						>
							<LocationAutocomplete
								initialValue={''}
								placeholder="9035 Village Dr, Yosemite Valley, CA 95389"
								onSelect={handleLocationAutocompleteSelect}
							/>
						</Form.Item>,
						<Form.Item name="primaryAddress-streetAddress1" hidden />,
						<Form.Item name="primaryAddress-streetAddress2Opt" hidden />,
						<Form.Item name="primaryAddress-city" hidden />,
						<Form.Item name="primaryAddress-region" hidden />,
						<Form.Item name="primaryAddress-postcode" hidden />,
						<Form.Item name="primaryAddress-country" hidden />,
						<Form.Item name="longitude" hidden />,
						<Form.Item name="latitude" hidden />,
						<Form.Item label="Website" name="website" style={formItemStyle}>
							<Input />
						</Form.Item>,
				  ]
				: null}
			<Form.Item>
				<OWButton type={'primary'} onClick={() => form.submit()} loading={loading}>
					{isUpdate ? 'Update' : 'Create'} Contractor Client
				</OWButton>
				<Button style={{ marginLeft: 12 }} onClick={onCancel}>
					Cancel
				</Button>
			</Form.Item>
		</Form>
	);
};

const mapStateToProps = (state) => ({
	users: state.users,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	create: (entity, primaryKey, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? contractorClientsRestCrudThunksForSupplier.create(
						entity,
						primaryKey,
						targetCollectionName
				  )
				: contractorClientsRestCrudThunksForBuyer.create(entity, primaryKey, targetCollectionName)
		),
	update: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? contractorClientsRestCrudThunksForSupplier.update(entity)
				: contractorClientsRestCrudThunksForBuyer.update(entity)
		),
	fetchUsers: (params, targetCollectionName, pagination, sorting, filters, addToTargetCollection) =>
		dispatch(
			usersRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchMultipleUsers: (ids, targetCollectionName) =>
		dispatch(usersRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
});

export default withUserType(
	connect(mapStateToProps, mapDispatchToProps)(ContractorClientForm as any) as any
);
